
import { supabase } from '@/lib/supabase/client';
import { ApiResponse, DetectionStats, HistoryQueryParams } from '../types/apiTypes';

/**
 * Get detection history for a specific user
 */
export const getUserDetectionHistory = async (userId: string | HistoryQueryParams): Promise<ApiResponse> => {
  try {
    let query = supabase
      .from('detection_results')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (typeof userId === 'string') {
      query = query.eq('user_id', userId);
    } else {
      // Handle object parameter with query options
      query = query.eq('user_id', userId.userId);
      
      if (userId.analysisType) {
        query = query.eq('analysis_type', userId.analysisType);
      }
      
      // Use the updated interface properties
      if (userId.startDate) {
        query = query.gte('created_at', userId.startDate.toISOString());
      }
      
      if (userId.endDate) {
        query = query.lte('created_at', userId.endDate.toISOString());
      }
      
      if (userId.limit) {
        query = query.limit(userId.limit);
      }
      
      if (userId.offset) {
        query = query.range(userId.offset, userId.offset + (userId.limit || 10) - 1);
      }
    }
    
    const { data, error, count } = await query;
      
    if (error) {
      throw error;
    }
    
    return { 
      success: true,
      data, 
      error: null,
      status: 200,
      count
    };
  } catch (error) {
    console.error('Error fetching user detection history:', error);
    return { 
      success: false,
      data: null, 
      error: error instanceof Error ? error.message : String(error),
      status: 500,
      count: null
    };
  }
};

/**
 * Get aggregated statistics about a user's detection history
 */
export const getUserDetectionStats = async (userId: string | HistoryQueryParams): Promise<ApiResponse<DetectionStats>> => {
  try {
    // This would ideally be a database function for efficiency
    // For now, we'll fetch all records and aggregate on the client
    const { data: detectionData, error } = await getUserDetectionHistory(userId);
    
    if (error) {
      throw error;
    }
    
    // Initialize with default values
    const stats: DetectionStats = {
      total_analyses: 0,
      high_risk_count: 0,
      avg_risk_score: 0,
      analyses_by_type: {},
      recent_flags: [],
      detection_trend: [],
      // Add the additional fields with proper naming
      totalDetections: 0,
      highRiskDetections: 0,
      dailyDetections: {},
      trend: 'Stable',
      recent_analyses: 0,
      average_score: 0
    };
    
    if (!detectionData || detectionData.length === 0) {
      return { 
        success: true,
        data: stats, 
        error: null,
        status: 200
      };
    }
    
    // Calculate stats from detection data
    stats.totalDetections = detectionData.length;
    stats.highRiskDetections = detectionData.filter(d => d.score > 0.7).length;
    
    // Set legacy field values to match the new fields
    stats.total_analyses = stats.totalDetections;
    stats.high_risk_count = stats.highRiskDetections;
    
    // Get the most recent analyses
    stats.recent_analyses = detectionData.slice(0, 5).length;
    
    // Calculate average score
    stats.average_score = detectionData.reduce((sum, d) => sum + (d.score || 0), 0) / detectionData.length;
    stats.avg_risk_score = stats.average_score;
    
    // Calculate daily detections
    const dailyCounts: Record<string, number> = {};
    detectionData.forEach(d => {
      const dateKey = new Date(d.created_at).toISOString().split('T')[0];
      dailyCounts[dateKey] = (dailyCounts[dateKey] || 0) + 1;
    });
    stats.dailyDetections = dailyCounts;
    
    // Determine trend (very simplified)
    const sortedDates = Object.keys(dailyCounts).sort();
    if (sortedDates.length > 1) {
      const latestDay = sortedDates[sortedDates.length - 1];
      const previousDay = sortedDates[sortedDates.length - 2];
      
      if (dailyCounts[latestDay] > dailyCounts[previousDay]) {
        stats.trend = 'Increasing';
      } else if (dailyCounts[latestDay] < dailyCounts[previousDay]) {
        stats.trend = 'Decreasing';
      }
    }
    
    return { 
      success: true,
      data: stats, 
      error: null,
      status: 200
    };
  } catch (error) {
    console.error('Error generating user detection stats:', error);
    // Return empty stats on error
    const emptyStats: DetectionStats = {
      total_analyses: 0,
      high_risk_count: 0,
      avg_risk_score: 0,
      analyses_by_type: {},
      recent_flags: [],
      detection_trend: [],
      // Add the additional fields with proper naming
      totalDetections: 0,
      highRiskDetections: 0,
      dailyDetections: {},
      trend: 'Stable',
      recent_analyses: 0,
      average_score: 0
    };
    return { 
      success: false,
      data: emptyStats, 
      error: error instanceof Error ? error.message : String(error),
      status: 500
    };
  }
};
